import { Inject, Injectable } from '@angular/core';

import { OddsAcceptanceMode } from '@bpos';
import { UserActions } from '@frontend/sports/user/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatMap, filter, ignoreElements, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs';

import { betslipSelector } from '../base/store/selectors';
import { IBetslipRootState } from '../base/store/state';
import { BetslipState } from '../core/betslip-state';
import { BetslipType } from '../core/betslip-type';
import { ExternalBetslipActions, PickAddPayload } from '../core/external-betslip-actions';
import { OddChangeConfirmationService } from '../modules/betting-preference/odd-changes-confirmation/odd-changes-confirmation.service';
import { EditBetActions } from '../modules/edit-bet/actions';
import { QuickBetActions } from '../modules/quick-bet/quick-bet-actions';
import { QuickBetUiState } from '../modules/quick-bet/quick-bet.state';
import { BetslipResultActions } from '../modules/result/actions';
import { BetslipTypesActions } from '../modules/types/actions';
import { BetslipModuleLoaderService } from './betslip-module-loader.service';
import { BETSLIP_MODULE_LOADER_SERVICE } from './sports-injection-services';

@Injectable()
export class ExternalBetslipEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private oddChangeConfirmationService: OddChangeConfirmationService,
        @Inject(BETSLIP_MODULE_LOADER_SERVICE) private betslipLoaderModule: BetslipModuleLoaderService,
    ) {}

    closeResultOnAddPickEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ExternalBetslipActions.addPick,
                ExternalBetslipActions.addMultiplePicks,
                ExternalBetslipActions.addBetBuilderPick,
                ExternalBetslipActions.addEntainUiBetBuilderPick,
                ExternalBetslipActions.requestAddAffiliatePicks,
                ExternalBetslipActions.addGroupPick,
            ),
            switchMap(() =>
                this.betslipLoaderModule.loadBetslipModule().pipe(
                    withLatestFrom(this.store.select(betslipSelector)),
                    filter(([_, currentState]) => currentState.base.state === BetslipState.Result),
                    concatMap(([_, currentState]) => [
                        BetslipTypesActions.setSelectedType({
                            betslipType: null,
                            selectedByUser: false,
                            systemInfo: null,
                        }),
                        BetslipTypesActions.setActiveTypes({ betslipTypes: [] }),
                        currentState.types.base.currentSelectedType === BetslipType.EditBet
                            ? EditBetActions.exitEditBet()
                            : BetslipResultActions.close(),
                    ]),
                ),
            ),
        ),
    );

    closeQuickBetConfirmationOnAddPickEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ExternalBetslipActions.addPick,
                ExternalBetslipActions.addMultiplePicks,
                ExternalBetslipActions.addBetBuilderPick,
                ExternalBetslipActions.addEntainUiBetBuilderPick,
                ExternalBetslipActions.requestAddAffiliatePicks,
                ExternalBetslipActions.addGroupPick,
            ),
            switchMap(() =>
                this.betslipLoaderModule.loadBetslipModule().pipe(
                    withLatestFrom(this.store.select(betslipSelector)),
                    filter(([_, currentState]) => currentState.quickBet.uiState === QuickBetUiState.Success),
                    map(() => QuickBetActions.closeResultScreen()),
                ),
            ),
        ),
    );

    handleAddPickActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.addPick),
            mergeMap((action) => this.getRequestAddPickActions([action])),
        ),
    );

    handleOddsConfirmationPopupDisplay$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ExternalBetslipActions.addPick,
                    ExternalBetslipActions.addMultiplePicks,
                    ExternalBetslipActions.addBetBuilderPick,
                    ExternalBetslipActions.addEntainUiBetBuilderPick,
                    ExternalBetslipActions.requestAddAffiliatePicks,
                    ExternalBetslipActions.addGroupPick,
                ),
                switchMap(() =>
                    this.oddChangeConfirmationService.isEligibleForOddsChangePopup().pipe(
                        filter((isEligible) => isEligible),
                        tap(() => this.oddChangeConfirmationService.checkAndOpenOddsChangePopup()),
                    ),
                ),
                ignoreElements(),
            ),
        { dispatch: false },
    );

    handleAddPicksActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.addMultiplePicks),
            switchMap((action) => this.getRequestAddPickActions(action.picks)),
        ),
    );

    handleAddGroupPickActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.addGroupPick),
            mergeMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(
                    map((module) =>
                        module.BetslipPickActions.requestAddGroupPick({
                            payload: action.picks,
                            headerTemplate: action.headerTemplate,
                        }),
                    ),
                ),
            ),
        ),
    );

    handleAddBetBuilderPickActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.addBetBuilderPick),
            switchMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(
                    map((module) =>
                        module.BetslipPickActions.requestAddBetBuilderPick({
                            pickId: action.pickId,
                            tracking: action.tracking,
                            eventData: action.eventData,
                            additionalInfo: action.additionalInfo,
                        }),
                    ),
                ),
            ),
        ),
    );

    handleAddEntainUiBetBuilderPickActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.addEntainUiBetBuilderPick),
            switchMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(
                    map((module) =>
                        module.BetslipPickActions.requestAddEntainUiBetBuilderPick({
                            pickId: action.pickId,
                            tracking: action.tracking,
                            eventData: action.eventData,
                            eventModel: action.eventModel,
                            market: action.betBuilderOptionMarket,
                            longId: [action.longId],
                        }),
                    ),
                ),
            ),
        ),
    );

    handleRemoveEntainUIPickActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.removeEntainUiBetBuilderPick),
            switchMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(
                    map((module) =>
                        module.BetslipPickActions.requestRemoveEntainUiBetBuilderPick({
                            pickId: action.pickId,
                            longId: action.longId,
                            eventId: action.eventId,
                        }),
                    ),
                ),
            ),
        ),
    );

    handleCheckPrecreatedBABConversion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.checkPrecreatedBABConversion),
            switchMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(
                    map((module) =>
                        module.BetslipPickActions.checkPrecreatedBABConversion({
                            pickId: action.pickId,
                            eventData: action.eventData,
                            eventModel: action.eventModel,
                            market: action.market,
                            longId: action.longId,
                            headerTemplate: action.headerTemplate,
                        }),
                    ),
                ),
            ),
        ),
    );

    handleRemovePickActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.removePick),
            switchMap((action) =>
                this.betslipLoaderModule
                    .loadBetslipModule()
                    .pipe(map((module) => module.BetslipPickActions.requestRemovePick({ pickId: action.pickId }))),
            ),
        ),
    );

    handleRemovePicksFromGroupActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.removePicksFromGroup),
            switchMap((action) =>
                this.betslipLoaderModule
                    .loadBetslipModule()
                    .pipe(map((module) => module.BetslipPickActions.requestRemovePicksFromGroup({ pickIds: action.pickIds }))),
            ),
        ),
    );

    handleAffiliateRequestActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.requestAddAffiliatePicks),
            switchMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(map((module) => module.BetslipPickActions.addAffiliatePicks(action))),
            ),
        ),
    );

    handleAffiliateShareBetRequestActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.shareBet),
            switchMap(() => this.betslipLoaderModule.loadBetslipModule().pipe(map((module) => module.BetslipPickActions.shareAffiliatePicks()))),
        ),
    );

    handleRemoveMultiplePicksActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.removeMultiplePicks),
            switchMap((action) =>
                this.betslipLoaderModule
                    .loadBetslipModule()
                    .pipe(map((module) => module.BetslipPickActions.requestRemoveMultiplePicks({ pickIds: action.pickIds }))),
            ),
        ),
    );

    handleStartEditMyBetActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.startEditMyBet),
            switchMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(map((module) => module.EditBetActions.startEditMyBet({ payload: action.payload }))),
            ),
        ),
    );

    handleNotificationSettingUpdateActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.updateState),
            switchMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(
                    map((module) =>
                        module.SettingsActions.updateNotificationSettings({
                            oddsAcceptance: action.settings.oddsAcceptanceMode ? <OddsAcceptanceMode>action.settings.oddsAcceptanceMode : undefined,
                            emailNotify: action.settings.betslipEmailNotification,
                            appNotify: action.settings.betslipAppNotification,
                        }),
                    ),
                ),
            ),
        ),
    );

    private getRequestAddPickActions(payload: PickAddPayload[]) {
        return this.betslipLoaderModule.loadBetslipModule().pipe(
            mergeMap((module) => [
                ...payload.map((y) =>
                    module.BetslipPickActions.requestAddPick({
                        pickId: y.pickId,
                        tracking: y.tracking,
                        priceId: y.priceId,
                        isPriceBoosted: y.isPriceBoosted,
                        groupInfo: y.groupInfo,
                        parentLinkedEventId: y.parentLinkedEventId,
                        isBetBuilderEnabled: y.isBetBuilder ?? false,
                    }),
                ),
                module.RewardTokensActions.closeDialog(),
            ]),
        );
    }

    handleAddWebAppPickActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.addWebAppPick),
            switchMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(map((module) => module.BetslipPickActions.addWebAppPick(action))),
            ),
        ),
    );

    handleRemoveWebAppPickActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.removeWebAppPick),

            switchMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(map((module) => module.BetslipPickActions.removeWebAppPick(action))),
            ),
        ),
    );

    handleSetRewardTokenActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.setBetstationFreeBetToken),
            switchMap((action) =>
                this.betslipLoaderModule.loadBetslipModule().pipe(map((module) => module.RewardTokensActions.setBetstationFreeBetToken(action))),
            ),
        ),
    );

    handleToggleWinPickActionEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalBetslipActions.toggleWinPick),
            switchMap((action) =>
                this.betslipLoaderModule
                    .loadBetslipModule()
                    .pipe(map((module) => module.BetslipPickActions.toggleWinPick({ pickId: action.pickId }))),
            ),
        ),
    );
    //
    // private loadPickFromApi(payload: IAddPickPayload): Observable<Action> {
    // 	const pickId = pickIdFactory(payload.pickId);
    // 	const effect = this;
    // 	const request = this.betslipPicksRequestBuilder.buildRequestFromPickId([pickId]);
    // 	return from(this.bettingOffer.getPicks(request)).pipe(
    // 		filter(fp => Boolean(fp && fp.fixtures.length)),
    // 		concatMap(function* (response: FixturePage | undefined): Iterable<Action> {
    // 			const fixture = response!.fixtures[0];
    // 			if (fixture) {
    // 				let priceId;
    // 				if (payload.extra.type === AddPickPayloadType.Race) {
    // 					priceId = payload.extra.priceId;
    // 				}
    // 				const betslipPick = effect.pickFactory.createPick(pickId, fixture, null, payload.extra.sourceOfBet, priceId);
    // 				if (!!betslipPick) {
    // 					yield PickActions.addedPick({ pick: betslipPick, pickId: betslipPick.id.toString(), priceId });
    // 				}
    // 			}
    // 		})
    // 	);
    // }
}
